import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

interface CompanySettings {
  company: string;
  company_name: string;
  company_caption: string;
  abbr: string;
  country: string;
  default_currency: string;
}

interface CompanyState {
  settings: CompanySettings | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: CompanyState = {
  settings: null,
  isLoading: false,
  error: null,
};

// Async thunk to fetch company settings
export const fetchCompanySettings = createAsyncThunk(
  'company/fetchSettings',
  async (_, { rejectWithValue }) => {
    try {
      const company = process.env.REACT_APP_COMPANY || 'QASTCO';
      const mcpServerUrl = process.env.VITE_MCP_SERVER_URL || 'https://vrestmcp.qastco.com';

      const response = await fetch(
        `${mcpServerUrl}/api/settings/company?company=${company}`
      );

      if (!response.ok) {
        const error = await response.json();
        return rejectWithValue(error.message);
      }

      const result = await response.json();

      if (!result.success) {
        return rejectWithValue(result.message || 'Failed to fetch company settings');
      }

      return result.data;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

const companySlice = createSlice({
  name: 'company',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCompanySettings.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCompanySettings.fulfilled, (state, action) => {
        state.isLoading = false;
        state.settings = action.payload;
      })
      .addCase(fetchCompanySettings.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError } = companySlice.actions;
export default companySlice.reducer;
